import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { VOICE_MESSAGES_COPY, storedUiLanguage } from './i18n/consoleChromeCopy';
import './VoiceMessagesPage.css';

interface VoiceMessage {
  greeting_text: string;
  ai_bot_prompt: string;
  ssml_prompt?: string;
  pronunciation_dictionary?: string;
  last_updated?: string;
  applied_to_contact_flow: boolean;
}

interface VoiceMessageHistoryEvent {
  actor_user_id?: string;
  event_type?: string;
  changed_at?: string;
  changes?: Record<string, unknown>;
}

interface VoiceVoicemail {
  id?: number;
  caller_number?: string;
  caller_name?: string;
  transcript?: string;
  recording_url?: string;
  left_at?: string;
  duration_seconds?: number;
  status?: string;
}

interface VoiceMessageUpdateRequest {
  greeting_text?: string;
  ai_bot_prompt?: string;
  ssml_prompt?: string;
  pronunciation_dictionary?: string;
  apply_immediately?: boolean;
}

const VoiceMessagesPage: React.FC = () => {
  const chrome = VOICE_MESSAGES_COPY[storedUiLanguage()];
  const { user, accessToken } = useAuth();
  const [voiceMessages, setVoiceMessages] = useState<VoiceMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editingGreeting, setEditingGreeting] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [newGreeting, setNewGreeting] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [applyImmediately, setApplyImmediately] = useState(false);
  const [historyItems, setHistoryItems] = useState<VoiceMessageHistoryEvent[]>([]);
  const [voicemails, setVoicemails] = useState<VoiceVoicemail[]>([]);
  const [loadingVoicemails, setLoadingVoicemails] = useState(false);
  const [showVoicemails, setShowVoicemails] = useState(false);
  const [ssmlSample, setSsmlSample] = useState(
    '<speak><say-as interpret-as="characters">SSO</say-as> support is available 24/7.</speak>'
  );
  const [pronunciationDictionary, setPronunciationDictionary] = useState(
    'NextCompli=next comply\nAML=anti money laundering\nKYC=know your customer'
  );

  const inferGatewayBaseUrl = (): string => {
    const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
    if (configured) {
      // Prefer IPv4 loopback locally — Cloudflare WARP / IPv6 localhost can stall fetches.
      if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
        return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
      }
      return configured.replace(/\/$/, '');
    }
    if (typeof window === 'undefined') {
      return 'http://127.0.0.1:8000';
    }
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
    if (isLocal) {
      return 'http://127.0.0.1:8000';
    }
    const apexHost = host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '');
    return `https://${apexHost}`;
  };

  const GATEWAY_BASE_URL = inferGatewayBaseUrl();
  const VOICE_MESSAGES_URL = `${GATEWAY_BASE_URL}/api/v1/admin/voice/messages`;
  const VOICE_HISTORY_URL = `${GATEWAY_BASE_URL}/api/v1/admin/voice/messages/history?limit=10`;
  const VOICE_VOICEMAILS_URL = `${GATEWAY_BASE_URL}/api/v1/admin/voice/voicemails?limit=25`;

  const sanitizeSsmlPreview = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    const withoutUnsafe = trimmed
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<audio[\s\S]*?>[\s\S]*?<\/audio>/gi, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return withoutUnsafe;
  };

  // Fetch current voice messages
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        const response = await fetch(VOICE_MESSAGES_URL, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch voice messages (${response.status})`);
        }

        const data: VoiceMessage = await response.json();
        setVoiceMessages(data);
        setNewGreeting(data.greeting_text);
        setNewPrompt(data.ai_bot_prompt);
        setSsmlSample(data.ssml_prompt || '');
        setPronunciationDictionary(data.pronunciation_dictionary || '');
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load voice messages');
      } finally {
        setLoading(false);
      }
    };

    if (accessToken) {
      fetchMessages();
    }
  }, [accessToken, VOICE_MESSAGES_URL]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(VOICE_HISTORY_URL, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          return;
        }
        const payload = await response.json();
        if (Array.isArray(payload?.history)) {
          setHistoryItems(payload.history as VoiceMessageHistoryEvent[]);
        }
      } catch {
        // Non-blocking history fetch.
      }
    };

    if (accessToken) {
      fetchHistory();
    }
  }, [accessToken, VOICE_HISTORY_URL]);

  useEffect(() => {
    const fetchVoicemails = async () => {
      try {
        setLoadingVoicemails(true);
        const response = await fetch(VOICE_VOICEMAILS_URL, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          return;
        }
        const payload = await response.json();
        if (Array.isArray(payload?.voicemails)) {
          setVoicemails(payload.voicemails as VoiceVoicemail[]);
        }
      } catch {
        // Non-blocking voicemail fetch.
      } finally {
        setLoadingVoicemails(false);
      }
    };

    if (accessToken && showVoicemails) {
      fetchVoicemails();
    }
  }, [accessToken, showVoicemails, VOICE_VOICEMAILS_URL]);

  const handleSaveMessages = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      const updatedFields: VoiceMessageUpdateRequest = {};
      if (editingGreeting && newGreeting !== voiceMessages?.greeting_text) {
        updatedFields.greeting_text = newGreeting;
      }
      if (editingPrompt && newPrompt !== voiceMessages?.ai_bot_prompt) {
        updatedFields.ai_bot_prompt = newPrompt;
      }

      const originalSsml = voiceMessages?.ssml_prompt || '';
      if (ssmlSample.trim() !== originalSsml.trim()) {
        updatedFields.ssml_prompt = ssmlSample.trim();
      }

      const originalDictionary = voiceMessages?.pronunciation_dictionary || '';
      if (pronunciationDictionary.trim() !== originalDictionary.trim()) {
        updatedFields.pronunciation_dictionary = pronunciationDictionary.trim();
      }

      if (Object.keys(updatedFields).length === 0) {
        setSuccessMessage('No changes to save.');
        return;
      }

      updatedFields.apply_immediately = applyImmediately;

      const response = await fetch(VOICE_MESSAGES_URL, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedFields),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody.detail || `Failed to update voice messages (${response.status})`
        );
      }

      const updated = await response.json();
      setVoiceMessages(updated);
      setSuccessMessage('Voice messages updated successfully. Changes will apply at next deployment.');
      setEditingGreeting(false);
      setEditingPrompt(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update voice messages');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return <div className="theme-admin-card"><p>{chrome.loginRequired}</p></div>;
  }

  return (
    <section className="page-section">
      <div className="theme-admin-card">
        <h1>{chrome.title}</h1>
        <p>{chrome.subtitle}</p>

        {loading ? (
          <p className="theme-admin-message">{chrome.loading}</p>
        ) : null}

        {error ? (
          <div className="voice-msg-alert voice-msg-alert--error">
            <strong>Error:</strong> {error}
          </div>
        ) : null}

        {successMessage ? (
          <div className="voice-msg-alert voice-msg-alert--success">
            <strong>Success:</strong> {successMessage}
          </div>
        ) : null}

        {voiceMessages && (
          <div>
            <div className="voice-msg-section">
              <div className="voice-msg-section-head">
                <h3>Greeting Message</h3>
                {!editingGreeting ? (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEditingGreeting(true)}
                  >
                    Edit
                  </button>
                ) : null}
              </div>

              {editingGreeting ? (
                <div style={{ marginBottom: '16px' }}>
                  <textarea
                    value={newGreeting}
                    onChange={(e) => setNewGreeting(e.target.value)}
                    className="voice-msg-textarea"
                    placeholder="Enter new greeting message..."
                  />
                  <p className="voice-msg-hint">
                    10-500 characters. Keep it conversational and clear for text-to-speech.
                  </p>
                </div>
              ) : (
                <div className="voice-msg-preview">
                  {voiceMessages.greeting_text}
                </div>
              )}
            </div>

            <div className="voice-msg-section">
              <div className="voice-msg-section-head">
                <h3>AI Bot Prompt</h3>
                {!editingPrompt ? (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setEditingPrompt(true)}
                  >
                    Edit
                  </button>
                ) : null}
              </div>

              {editingPrompt ? (
                <div style={{ marginBottom: '16px' }}>
                  <textarea
                    value={newPrompt}
                    onChange={(e) => setNewPrompt(e.target.value)}
                    className="voice-msg-textarea"
                    placeholder="Enter new AI bot prompt..."
                  />
                  <p className="voice-msg-hint">
                    10-500 characters. Guide the caller on how to interact with the AI assistant.
                  </p>
                </div>
              ) : (
                <div className="voice-msg-preview">
                  {voiceMessages.ai_bot_prompt}
                </div>
              )}
            </div>

            {(editingGreeting || editingPrompt) && (
              <div className="voice-msg-panel voice-msg-panel--deploy">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={applyImmediately}
                    onChange={(e) => setApplyImmediately(e.target.checked)}
                  />
                  <span style={{ fontSize: '14px' }}>
                    <strong>Deploy immediately</strong> (trigger Terraform update via pipeline)
                  </span>
                </label>
                <p className="voice-msg-hint">
                  If unchecked, changes will be queued for approval and applied at the next scheduled deployment window.
                </p>
              </div>
            )}

            {(editingGreeting || editingPrompt) && (
              <div className="voice-msg-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditingGreeting(false);
                    setEditingPrompt(false);
                    setNewGreeting(voiceMessages.greeting_text);
                    setNewPrompt(voiceMessages.ai_bot_prompt);
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveMessages}
                  disabled={submitting}
                >
                  {submitting ? chrome.saving : chrome.save}
                </button>
              </div>
            )}

            <div className="voice-msg-panel" style={{ marginTop: '32px' }}>
              <h4>ℹ️ How This Works</h4>
              <ul>
                <li>Changes to voice messages are logged in the audit trail.</li>
                <li>Messages are applied to Amazon Connect contact flows during deployment.</li>
                <li>Text-to-speech quality: keep messages natural, avoid abbreviations and special characters.</li>
                <li>Maximum length: 500 characters per message (current: {newGreeting.length}/{newPrompt.length}).</li>
                <li>Changes require platform admin approval for security compliance.</li>
              </ul>
            </div>

            <div className="voice-msg-panel voice-msg-panel--info">
              <h4>Best Practices</h4>
              <ul>
                <li>Keep prompts short and action-first: state intent, then options, then repeat once.</li>
                <li>Use consistent menu numbering so repeat callers learn your IVR path.</li>
                <li>Avoid acronyms unless expanded, or add explicit pronunciation mappings.</li>
                <li>Use staged deployment: test in staging first, then apply in production.</li>
                <li>Review voicemail transcripts weekly to identify confusing prompts.</li>
              </ul>
            </div>

            <div className="voice-msg-panel voice-msg-panel--warn">
              <h4>DTMF and Spoken Selection Setup</h4>
              <p>
                The inbound flow already gathers both speech and keypad input. Use prompts like:
                {' '}<strong>"Press 1 for Sales, 2 for Support, 3 for Billing, or say Sales, Support, or Billing."</strong>
              </p>
              <ol>
                <li>Put your options in Greeting Message and AI Bot Prompt using both press and say instructions.</li>
                <li>In Amazon Lex, add intents for each destination and include utterances for numeric and spoken variants.</li>
                <li>If no input is detected, repeat options once, then route to voicemail.</li>
              </ol>
            </div>

            <div className="voice-msg-panel voice-msg-panel--ok">
              <h4>Language Detection and AI Routing</h4>
              <ul>
                <li>Incoming caller number can be mapped by E.164 prefix to a Lex locale (for example +1 to en_US, +34 to es_ES).</li>
                <li>Country code is a strong first signal but not perfect. Always add a fallback prompt like: "For English press 1, para Espanol oprima 2."</li>
                <li>Spoken-selection AI uses Lex intents. Add utterances for both words and numbers, such as "sales", "support", "billing", "one", "two", "three".</li>
                <li>Track no-match and fallback rates from voicemails and adjust prompts monthly.</li>
              </ul>
            </div>

            <div className="voice-msg-panel voice-msg-panel--ok">
              <h4>Pronunciation and SSML Preview</h4>
              <p>
                Use this section to prepare advanced pronunciation before approval. Unsafe tags are removed in preview.
              </p>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>SSML Draft</label>
              <textarea
                value={ssmlSample}
                onChange={(e) => setSsmlSample(e.target.value)}
                className="voice-msg-ssml-textarea"
              />
              <p className="voice-msg-ssml-hint">
                Sanitized preview text
              </p>
              <div className="voice-msg-ssml-preview">
                {sanitizeSsmlPreview(ssmlSample) || 'No preview text yet.'}
              </div>

              <label style={{ display: 'block', marginTop: '12px', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
                Pronunciation Dictionary (term=spoken form)
              </label>
              <textarea
                value={pronunciationDictionary}
                onChange={(e) => setPronunciationDictionary(e.target.value)}
                className="voice-msg-ssml-textarea"
              />
              <p className="voice-msg-ssml-hint" style={{ marginBottom: 0 }}>
                Example: KYC=know your customer, OFAC=oh-fak.
              </p>
            </div>

            <div className="voice-msg-panel voice-msg-panel--info">
              <div className="voice-msg-voicemail-head">
                <h4 style={{ margin: 0 }}>Voicemail Inbox</h4>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowVoicemails((current) => !current)}>
                  {showVoicemails ? 'Hide Voicemails' : 'Show Voicemails'}
                </button>
              </div>
              {showVoicemails ? (
                <div style={{ marginTop: '12px' }}>
                  {loadingVoicemails ? <p style={{ margin: 0 }}>{chrome.loadingVoicemails}</p> : null}
                  {!loadingVoicemails && voicemails.length === 0 ? <p style={{ margin: 0 }}>No voicemails found.</p> : null}
                  {!loadingVoicemails && voicemails.length > 0 ? (
                    <div className="voice-msg-voicemail-list">
                      {voicemails.map((item, index) => (
                        <div
                          key={`${item.id ?? 'vm'}-${index}`}
                          className="voice-msg-voicemail-card"
                        >
                          <div style={{ fontSize: '13px', marginBottom: '6px' }}>
                            <strong>{item.caller_name || 'Unknown caller'}</strong>
                            {' '}({item.caller_number || 'Unknown number'})
                          </div>
                          <div className="voice-msg-voicemail-meta">
                            {item.left_at || 'Unknown time'}
                            {' '}
                            {item.duration_seconds ? `• ${item.duration_seconds}s` : ''}
                            {' '}
                            {item.status ? `• ${item.status}` : ''}
                          </div>
                          <div style={{ fontSize: '13px', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                            {item.transcript || 'No transcript available.'}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="voice-msg-panel">
              <h4>Recent Change History</h4>
              {historyItems.length === 0 ? (
                <p style={{ margin: 0, fontSize: '14px' }}>No recent history events yet.</p>
              ) : (
                <div className="voice-msg-history-list">
                  {historyItems.map((entry, index) => (
                    <div key={`${entry.event_type ?? 'event'}-${index}`}>
                      <strong>{entry.event_type || 'voice_message_event'}</strong>
                      {' '}by {entry.actor_user_id || 'unknown user'}
                      {' '}at {entry.changed_at || 'unknown time'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  )

};

export default VoiceMessagesPage;
